'use client'
import { useTranslate } from '../../hooks'
import { Icon, NumberUnit, Separator } from '@repo/ui/components'
import { Col } from '@repo/ui/components/col'
import { Row } from '@repo/ui/components/row'
import { Typography } from '@repo/ui/components/typography'
import type { CSSVariableColor } from '@repo/ui/types/colors'
import { Fragment, useMemo, useState } from 'react'
import { OrderbookOrdersEmpty } from './orderbook.orders-empty'

const mockOrders = [
  // Sell orders (10 records)
  { price: 73500, amount: 20, total: 20, type: 'sell' },
  { price: 73200, amount: 240, total: 20, type: 'sell' },
  { price: 73000, amount: 5000, total: 240, type: 'sell' },
  { price: 72800, amount: 100, total: 5000, type: 'sell' },
  { price: 72600, amount: 1500, total: 2000, type: 'sell' },
  { price: 72400, amount: 1500, total: 2000, type: 'sell' },
  { price: 72200, amount: 30, total: 120, type: 'sell' },
  { price: 72000, amount: 100000, total: 12, type: 'sell' },
  { price: 71800, amount: 50, total: 600, type: 'sell' },
  { price: 71600, amount: 200, total: 800, type: 'sell' },

  // Buy orders (10 records)
  { price: 71300, amount: 100, total: 5000, type: 'buy' },
  { price: 71000, amount: 1500, total: 2000, type: 'buy' },
  { price: 70800, amount: 1500, total: 2000, type: 'buy' },
  { price: 70500, amount: 30, total: 120, type: 'buy' },
  { price: 70200, amount: 100000, total: 12, type: 'buy' },
  { price: 70000, amount: 50, total: 600, type: 'buy' },
  { price: 69800, amount: 200, total: 800, type: 'buy' },
  { price: 69500, amount: 75, total: 450, type: 'buy' },
  { price: 69200, amount: 300, total: 1200, type: 'buy' },
  { price: 69000, amount: 120, total: 720, type: 'buy' },
]

interface OrderbookOrdersProps {
  hiddenAmount?: boolean
}

type DisplayOrder = 'both' | 'buy' | 'sell'

export function OrderbookOrders(props: OrderbookOrdersProps) {
  const { hiddenAmount } = props
  const { t } = useTranslate()
  const [displayOrder, setDisplayOrder] = useState<DisplayOrder>('both')

  // Filter and sort orders based on display order
  const sortedOrders = useMemo(() => {
    let orders = [...mockOrders]

    // Filter based on display order
    switch (displayOrder) {
      case 'buy':
        orders = orders.filter((order) => order.type === 'buy')
        break
      case 'sell':
        orders = orders.filter((order) => order.type === 'sell')
        break
      case 'both':
      default: {
        // For 'both' mode, limit to 4 sell and 4 buy orders
        const sellOrders = orders
          .filter((order) => order.type === 'sell')
          .slice(0, 5)
        const buyOrders = orders
          .filter((order) => order.type === 'buy')
          .slice(0, 6)
        orders = [...sellOrders, ...buyOrders]
        break
      }
    }

    // Sort orders: sell orders first (higher prices), then buy orders (lower prices)
    return orders.sort((a, b) => {
      if (a.type === 'sell' && b.type === 'buy') return -1
      if (a.type === 'buy' && b.type === 'sell') return 1
      // Within same type, sort by price
      if (a.type === 'sell') return b.price - a.price // sell: higher prices first
      return a.price - b.price // buy: lower prices first
    })
  }, [displayOrder])

  // اگر دیتا خالی بود:
  if (!mockOrders.length) return <OrderbookOrdersEmpty type='all' />

  const colors = {
    sell: {
      text: 'text-negative-surface-100',
      icon: 'fill-negative-surface-100',
      bg: 'bg-negative-surface-5',
    },
    buy: {
      text: 'text-positive-surface-100',
      icon: 'fill-positive-surface-100',
      bg: 'bg-positive-surface-5',
    },
  }

  return (
    <>
      {/* Mobile Display Order Controls */}
      <Row className='gap-2xs justify-end sm:justify-start'>
        <div
          className={`p-xs rounded-xs cursor-pointer max-w-2xl max-h-2xl flex items-center justify-center ${
            displayOrder === 'both'
              ? 'bg-neutral-surface-5'
              : 'hover:bg-neutral-surface-5'
          }`}
          onClick={() => setDisplayOrder('both')}
        >
          <Icon name='All' size='xs' />
        </div>
        <div
          className={`p-xs rounded-xs cursor-pointer max-w-2xl max-h-2xl flex items-center justify-center ${
            displayOrder === 'buy'
              ? 'bg-neutral-surface-5'
              : 'hover:bg-neutral-surface-5'
          }`}
          onClick={() => setDisplayOrder('buy')}
        >
          <Icon name='BuyOnly' size='xs' />
        </div>
        <div
          className={`p-xs rounded-xs cursor-pointer max-w-2xl max-h-2xl flex items-center justify-center ${
            displayOrder === 'sell'
              ? 'bg-neutral-surface-5'
              : 'hover:bg-neutral-surface-5'
          }`}
          onClick={() => setDisplayOrder('sell')}
        >
          <Icon name='SellOnly' size='xs' />
        </div>
      </Row>

      <Col className='mt-md'>
        <Row className='pb-xs justify-between items-center'>
          <Row className='gap-lg'>
            <Col>
              <Typography size='xs' color='text-neutral-surface-80'>
                {t('orderbook_price')}
              </Typography>
            </Col>
            {!hiddenAmount && (
              <Col>
                <Typography size='xs' color='text-neutral-surface-80'>
                  {t('orderbook_amount')}
                </Typography>
              </Col>
            )}
            <Col>
              <Typography
                size='xs'
                color='text-neutral-surface-80'
                className='flex justify-end'
              >
                {t('orderbook_total')}
              </Typography>
            </Col>
          </Row>
        </Row>
        <div className='overflow-auto scrollbar-hide h-[330px] sm:h-[420px]'>
          {!sortedOrders.some((order) => order.type === 'sell') &&
            displayOrder === 'both' && (
              <>
                <OrderbookOrdersEmpty type='sell' />
                <Separator />
              </>
            )}

          {sortedOrders.map((order, idx) => {
            const colorItem = colors[order.type as keyof typeof colors]
            const color = colorItem.text as CSSVariableColor
            const colorBG = colorItem.bg as CSSVariableColor
            const colorIcon = colorItem.icon as CSSVariableColor
            const middleIndex = Math.floor(sortedOrders.length / 2)

            if (idx === middleIndex && displayOrder === 'both') {
              return (
                <Fragment key={idx}>
                  <Separator />
                  <Row className='justify-between my-xs px-2xs'>
                    <Row className='w-fit flex-row-reverse rtl:flex-row'>
                      <Icon
                        name='TriangleUp'
                        size='xs'
                        className='-mr-xs'
                        color={colorIcon}
                      />
                      <NumberUnit
                        value={71460}
                        size='lg'
                        numberSize='lg'
                        numberColor={color}
                      />
                    </Row>
                    <Typography
                      size='xs'
                      color='text-neutral-surface-80'
                      className='whitespace-nowrap'
                    >
                      {t('orderbook_price_per_meter')}
                    </Typography>
                  </Row>
                  <Separator />
                </Fragment>
              )
            }
            return (
              <Row key={idx} className={`relative py-xs px-2xs ${color}`}>
                <div
                  className={`absolute top-0 right-0 h-full ${colorBG}`}
                  style={{ width: `${Math.round(Math.random() * 100)}%` }}
                />
                <Col>
                  <NumberUnit
                    value={order.price}
                    size='sm'
                    numberSize='sm'
                    numberColor={color}
                    isBold={false}
                  />
                </Col>
                {!hiddenAmount && (
                  <Col>
                    <NumberUnit
                      value={order.amount}
                      size='sm'
                      numberSize='sm'
                      numberColor={color}
                      isBold={false}
                    />
                  </Col>
                )}
                <Col>
                  <NumberUnit
                    value={order.total}
                    size='sm'
                    numberSize='sm'
                    numberColor={color}
                    numberClassName='justify-end'
                    className='justify-end'
                    isBold={false}
                  />
                </Col>
              </Row>
            )
          })}

          {!sortedOrders.some((order) => order.type === 'buy') &&
            displayOrder === 'both' && (
              <>
                <Separator />
                <OrderbookOrdersEmpty type='buy' />
              </>
            )}
        </div>
      </Col>
    </>
  )
}
